import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTheme, styled } from "@monolith-forensics/monolith-ui";
import { ChevronsRight, EditIcon } from "lucide-react";
import { MouseEvent, ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import InquiryColumnDefs from "@/components/Inquiries/InquiryColumnDefs.js";
import TaskButton from "@/Monolith-UI/TaskButton.js";
import { EditInquiryModal } from "@/Features";
import type { Inquiry } from "@/Features/Inquiries/inquiries.types";
import { monolithMoment } from "@/utils/date-format";
import resolveCustomFieldValue from "@/utils/customFieldValueResolver.js";
import { usePermissions } from "@/hooks/usePermissions";

import OverviewContent from "./components/OverviewContent.js";
import EvidenceItemsContent from "./components/EvidenceContent.js";
import DocumentContent from "./components/DocumentContent.js";
import PeopleContent from "./components/PeopleContent.js";

interface InquiryOverviewProps {
  className?: string;
  details: Inquiry;
}

interface SideContentProps {
  className?: string;
  details: Inquiry;
  readOnly?: boolean;
}

interface MainContentProps {
  className?: string;
  details: Inquiry;
  readOnly?: boolean;
}

const InquiryOverview = styled(
  ({ className, details }: InquiryOverviewProps) => {
    const readOnly = Boolean(details?.case_id);

    return (
      <div className={className}>
        <SideContent details={details} readOnly={readOnly} />
        <MainContent details={details} readOnly={readOnly} />
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  min-height: calc(100vh - 200px);
`;

const SideContent = styled(
  ({ className, details, readOnly }: SideContentProps) => {
    const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
    const theme: any = useTheme();
    let columnDefs = InquiryColumnDefs;

    const [open, setOpen] = useState(true);

    const [showEditModal, setShowEditModal] = useState(false);

    const dataFieldIgnoreList = [
      "description",
      "case_number",
      "case_ref",
      "inquiry_name",
      "inquiry_org",
      "inquiry_email",
      "inquiry_referred",
      "inquiry_client_type",
      "inquiry_notes",
    ];

    const inquiryInfo: Partial<Inquiry> = {
      ...details,
    };

    delete inquiryInfo.custom_attributes;
    delete inquiryInfo.case_ref;
    delete inquiryInfo.case_number;

    const requestorInfo = {
      inquiry_name: details?.inquiry_name,
      inquiry_org: details?.inquiry_org,
      inquiry_email: details?.inquiry_email,
      inquiry_referred: details?.inquiry_referred,
      inquiry_client_type: details?.inquiry_client_type,
    };

    const requestorDetails = (
      <div className="detail-container">
        {columnDefs.map((c) => {
          if (!requestorInfo[c.dataField as keyof typeof requestorInfo])
            return null;
          return (
            <div className="detail-item" key={c.dataField}>
              <div className="detail-label">{c.caption}</div>
              {c.render ? (
                c.render(details)
              ) : c.dataType === "date" ? (
                <>
                  {monolithMoment({
                    timestamp:
                      requestorInfo[c.dataField as keyof typeof requestorInfo],
                    includeTime: true,
                  })}
                </>
              ) : (
                <div className="detail-value">
                  {requestorInfo[c.dataField as keyof typeof requestorInfo]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );

    const caseInfo = {
      case_number: details?.case_number,
      case_ref: details?.case_ref,
    };

    const evidenceDetails = (
      <div className="detail-container">
        {columnDefs
          .filter((c) => !dataFieldIgnoreList.includes(c.dataField))
          .map((c) => {
            return (
              <div className="detail-item" key={c.dataField}>
                <div className="detail-label">{c.caption}</div>
                {c.render ? (
                  c.render(inquiryInfo)
                ) : c.dataType === "date" ? (
                  <>
                    {!!inquiryInfo[c.dataField as keyof Inquiry] &&
                      monolithMoment({
                        timestamp: inquiryInfo[c.dataField as keyof Inquiry] as
                          | string
                          | number
                          | undefined,
                        includeTime: true,
                      })}
                  </>
                ) : (
                  <div className="detail-value">
                    {inquiryInfo[c.dataField as keyof Inquiry] as ReactNode}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );

    const caseDetails = (
      <div className="detail-container">
        {columnDefs.map((c) => {
          if (!caseInfo[c.dataField as keyof typeof caseInfo]) return null;
          return (
            <div className="detail-item" key={c.dataField}>
              <div className="detail-label">{c.caption}</div>
              {c.render ? (
                c.render(details)
              ) : c.dataType === "date" ? (
                <>
                  {monolithMoment({
                    timestamp: caseInfo[c.dataField as keyof typeof caseInfo],
                    includeTime: true,
                  })}
                </>
              ) : (
                <div className="detail-value">
                  {caseInfo[c.dataField as keyof typeof caseInfo]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );

    const customFieldData =
      details?.custom_attributes?.filter((c) =>
        c.value == null || c.value === "" || c.value === "[]" ? false : true
      ) || [];

    const customDetails = (
      <div className="detail-container">
        {customFieldData.map((c) => (
          <div className="detail-item" key={c.field_id}>
            <div className="detail-label">{c.field_name}</div>
            <div className="detail-value">
              {resolveCustomFieldValue(c, { includeTime: true })}
            </div>
          </div>
        ))}
        {customFieldData.length === 0 && (
          <div className="detail-item">
            <div className="detail-label">No custom data</div>
          </div>
        )}
      </div>
    );

    return (
      <div className={className + (open ? " expanded" : " collapsed")}>
        <div
          className={
            open
              ? "collapse-btn side-content-expanded"
              : "collapse-btn side-content-collapsed"
          }
        >
          {open && <div className="header">Requestor Info</div>}
          <TaskButton
            variant="outlined"
            onClick={() => setOpen(!open)}
            style={{ marginLeft: "auto" }}
          >
            <ChevronsRight size={16} />
          </TaskButton>
        </div>
        {open && (
          <>
            {requestorDetails}
            {details.case_id && (
              <>
                <div className="header-container">
                  <div className="header">Associated Case</div>
                </div>
                {caseDetails}
              </>
            )}
            <div className="header-container">
              <div className="header" style={{ marginRight: 15 }}>
                Inquiry Details
              </div>
              {!readOnly &&
                hasPermission(MONOLITH_PERMISSIONS.INQUIRIES_UPDATE) && (
                  <button
                    type="button"
                    className="action-menu-item"
                    onClick={() => setShowEditModal(true)}
                    aria-label="Edit inquiry"
                  >
                    <EditIcon
                      size={15}
                      style={{ color: theme.palette.primary.main }}
                    />
                    <div className="action-menu-label">Edit</div>
                  </button>
                )}
            </div>
            {evidenceDetails}
            {customFieldData.length > 0 && (
              <div
                className="detail-label"
                style={{ marginTop: 16, marginBottom: 4 }}
              >
                Custom Fields
              </div>
            )}
            {customDetails}
            <EditInquiryModal
              show={showEditModal}
              onClose={() => setShowEditModal(false)}
              defaultInfo={details}
            />
          </>
        )}
      </div>
    );
  }
)`
  max-width: 425px;
  min-width: 425px;
  min-height: 350px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  overflow-y: auto;
  overflow-x: auto;
  transition: all 0.1s ease-in-out;

  &.collapsed {
    min-width: 50px;
    max-width: 50px;
  }

  & .collapse-btn {
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: space-between;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  & .side-content-expanded {
    svg {
      transform: rotate(180deg);
    }
  }

  & .side-content-collapsed {
    justify-content: center;
  }

  & .header {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1.1rem;
    font-weight: 500;
    cursor: default;
  }
  & .header-container {
    display: flex;
    align-items: center;
    align-content: center;
    margin: 20px 0px;
  }
  & .action-menu-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin-left: auto;
    cursor: pointer;
    border-radius: 5px;
    font-size: 12px;
    background: transparent;
    border: none;
    padding: 0;
    color: inherit;
    font-family: inherit;
    &:hover {
      text-decoration: underline;
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.palette.primary.main};
      outline-offset: 2px;
    }
    & svg {
      font-size: 15px;
      margin-right: 5px;
    }
  }
  & .detail-item {
    display: flex;
    align-items: baseline;
    align-content: center;
    margin: 10px 0px;
    font-size: 12px;
  }
  & .detail-label {
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 150px;
  }
  & .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
    width: 100%;
    white-space: pre-wrap;
  }
`;

const MainContent = styled(
  ({ className, details, readOnly }: MainContentProps) => {
    const { inquiry_id } = useParams();
    const [currentTab, setCurrentTab] = useState(0);

    const buttonStyle = {
      minWidth: "150px",
      padding: "2px 5px",
    };

    const handleChange = (
      _event: MouseEvent<HTMLElement>,
      newGroup: number | null
    ) => {
      if (newGroup !== null) {
        setCurrentTab(newGroup);
      }
    };

    const control = {
      value: currentTab,
      onChange: handleChange,
      exclusive: true,
    };

    useEffect(() => {
      setCurrentTab(0);
    }, [inquiry_id]);

    return (
      <div className={className}>
        <div className="toggle-menu">
          <ToggleButtonGroup size="small" {...control}>
            <ToggleButton
              value={0}
              size="small"
              disableRipple={true}
              style={buttonStyle}
            >
              Overview
            </ToggleButton>
            <ToggleButton
              value={1}
              size="small"
              disableRipple={true}
              style={buttonStyle}
            >
              <>Evidence</>
            </ToggleButton>
            <ToggleButton
              value={2}
              size="small"
              disableRipple={true}
              style={buttonStyle}
            >
              <>Documents</>
            </ToggleButton>
            <ToggleButton
              value={3}
              size="small"
              disableRipple={true}
              style={buttonStyle}
            >
              <>Contacts</>
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        {currentTab === 0 && (
          <OverviewContent inquiryInfo={details} readOnly={readOnly} />
        )}
        {currentTab === 1 && (
          <EvidenceItemsContent
            defaultInquiryInfo={details}
            readOnly={readOnly}
          />
        )}
        {currentTab === 2 && (
          <DocumentContent defaultInquiryInfo={details} readOnly={readOnly} />
        )}
        {currentTab === 3 && (
          <PeopleContent defaultInquiryInfo={details} readOnly={readOnly} />
        )}
      </div>
    );
  }
)`
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0px;

  & .toggle-menu {
    margin: 0 auto;
    margin-bottom: 25px;
    display: flex;
    overflow-x: auto;
    min-height: 30px;
  }
  & .header {
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 1.1rem;
    font-weight: 500;
  }
  & .description {
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 12px;
    margin-top: 10px;
  }
`;

export default InquiryOverview;
